/**
 * Binetic Control Center - Main Application
 * Supports role-based navigation and dual interface design
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Activity,
  GitBranch,
  Dna,
  Shield,
  ScrollText,
  Settings,
  Zap,
  Database,
  Server,
  Key,
  FileText,
  X,
  User,
  BarChart,
} from "lucide-react";
import { UnifiedAuthGate } from "@/components/UnifiedAuthGate";
import { AGIQuickAccess } from "@/components/AGIQuickAccess";
import { UserRegistration } from "@/components/UserRegistration";
import { RoleBadge } from "@/components/AuthGuard";
import { NotificationSystem, NotificationBell } from "@/components/NotificationSystem";
import { CommandPalette } from "@/components/CommandPalette";
import { DashboardView } from "@/views/DashboardView";
import { OperatorsView } from "@/views/OperatorsView";
import { PatternsView } from "@/views/PatternsView";
import { SecurityView } from "@/views/SecurityView";
import { SettingsView } from "@/views/SettingsView";
import { DiscoveryView } from "@/views/DiscoveryView";
import { EvolutionView } from "@/views/EvolutionView";
import { UserDashboardView } from "@/views/UserDashboardView";
import { MyApiKeysView } from "@/views/MyApiKeysView";
import { UserSettingsView } from "@/views/UserSettingsView";
import { PlaygroundView } from "@/views/PlaygroundView";
import { McpServersView } from "@/views/McpServersView";
import { DataView } from "@/views/DataView";
import { ActionLogView } from "@/views/ActionLogView";
import { PoliciesView } from "@/views/PoliciesView";
import { AdminApiKeysView } from "@/views/AdminApiKeysView";
import { useDashboardStore } from "@/store";
import { cn } from "@/lib/utils";
import type { AuthLevel } from "@/types";

interface NavItem {
  id: string;
  label: string;
  icon: any;
  roles: AuthLevel[];
}

// Admin navigation items (master/creator)
const ADMIN_NAV_ITEMS: NavItem[] = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["master", "creator"] },
  { id: "playground", label: "AGI Playground", icon: Zap, roles: ["master", "creator", "user", "passkey"] },
  { id: "mcp", label: "MCP Servers", icon: Server, roles: ["master", "creator"] },
  { id: "discovery", label: "Discovery", icon: Activity, roles: ["master", "creator"] },
  { id: "management", label: "Operators", icon: GitBranch, roles: ["master", "creator"] },
  { id: "data", label: "Data", icon: Database, roles: ["master", "creator"] },
  { id: "patterns", label: "Patterns", icon: Activity, roles: ["master", "creator"] },
  { id: "evolution", label: "Evolution", icon: Dna, roles: ["master", "creator"] },
  { id: "logs", label: "Action Log", icon: ScrollText, roles: ["master", "creator"] },
  { id: "apikeys", label: "API Keys", icon: Key, roles: ["master", "creator"] },
  { id: "policies", label: "Policies", icon: FileText, roles: ["master", "creator"] },
  { id: "security", label: "Security", icon: Shield, roles: ["master", "creator", "user", "passkey"] },
  { id: "settings", label: "Settings", icon: Settings, roles: ["master", "creator", "user", "passkey"] },
];

// User navigation items (user/passkey)
const USER_NAV_ITEMS: NavItem[] = [
  { id: "user-dashboard", label: "Dashboard", icon: BarChart, roles: ["user", "passkey"] },
  { id: "playground", label: "AGI Playground", icon: Zap, roles: ["master", "creator", "user", "passkey"] },
  { id: "my-api-keys", label: "My API Keys", icon: Key, roles: ["user", "passkey"] },
  { id: "user-settings", label: "Settings", icon: Settings, roles: ["master", "creator", "user", "passkey"] },
];

export default function App() {
  const [showRegistration, setShowRegistration] = useState(false);
  const { initialize, setActiveTab, addLogEntry } = useDashboardStore();
  
  const {
    activeTab,
    sidebarOpen,
    toggleSidebar,
    health,
    authLevel,
    systemMode,
  } = useDashboardStore();
  
  // Get filtered nav items based on role
  const getNavItems = (): NavItem[] => {
    if (authLevel === "master" || authLevel === "creator") {
      return ADMIN_NAV_ITEMS;
    }
    if (authLevel === "user" || authLevel === "passkey") {
      return USER_NAV_ITEMS;
    }
    // Unauthenticated - show minimal
    return [];
  };
 
  // Initialize on mount
  useEffect(() => {
    const { initialize } = useDashboardStore.getState();
    initialize();
 
    // Global logout handler
    (window as any).__bineticLogout = () => {
      addLogEntry({
        category: "security",
        action: "Logout",
        details: "User logged out",
        severity: "info",
        source: "app",
      });
    };
  }, [addLogEntry]);
 
  // Set default tab based on role
  useEffect(() => {
    if (authLevel !== "none" && activeTab === "dashboard") {
      // Switch to appropriate dashboard
      if (authLevel === "user" || authLevel === "passkey") {
        setActiveTab("user-dashboard");
      }
    }
  }, [authLevel, activeTab, setActiveTab]);
 
  return (
    <UnifiedAuthGate onAuthSuccess={() => {}}>
      {/* User Registration Modal */}
      <AnimatePresence>
        {showRegistration && (
          <UserRegistration
            onSuccess={() => setShowRegistration(false)}
            onCancel={() => setShowRegistration(false)}
          />
        )}
      </AnimatePresence>
      
      {/* AGI Quick Access - Always accessible */}
      <AGIQuickAccess />
 
      <div className="flex h-screen overflow-hidden bg-[hsl(var(--background))]">
        {/* Sidebar */}
        <motion.aside
          initial={false}
          animate={{ width: sidebarOpen ? 260 : 64 }}
          className="flex flex-col border-r border-[hsl(var(--border))] bg-[hsl(var(--card))]"
        >
          {/* Logo */}
          <div className="flex h-16 items-center justify-between border-b border-[hsl(var(--border))] px-4">
            {sidebarOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity:1 }}
                className="flex items-center gap-2"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--primary))] glow-green">
                  <Activity className="h-5 w-5 text-black" />
                </div>
                <span className="font-bold tracking-tight">BINETIC</span>
              </motion.div>
            )}
            <button
              onClick={toggleSidebar}
              className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(var(--muted))] cursor-pointer"
            >
              <LayoutDashboard className="h-4 w-4" />
            </button>
          </div>
 
          {/* Navigation */}
          <nav className="flex-1 space-y-1 p-2 overflow-y-auto">
            <AnimatePresence mode="popLayout">
              {getNavItems().map((item, index) => (
                <motion.button
                  key={item.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity:1, x: 0 }}
                  exit={{ opacity:0, x: 20 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => setActiveTab(item.id)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors cursor-pointer",
                    activeTab === item.id
                      ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"
                      : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]"
                  )}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <AnimatePresence>
                    {sidebarOpen && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity:1, width: "auto" }}
                        exit={{ opacity:0, width: 0 }}
                        className="overflow-hidden"
                      >
                        {item.label}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.button>
              ))}
            </AnimatePresence>
          </nav>
 
          {/* Footer */}
          <AnimatePresence>
            {sidebarOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity:1, height: "auto" }}
                exit={{ opacity:0, height: 0 }}
                className="border-t border-[hsl(var(--border))] p-4 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <RoleBadge role={authLevel} size="sm" />
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">{systemMode} mode</span>
                </div>
                {health && (
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">
                    {health.kernel?.healthy && health.convex?.healthy
                      ? "Dual mode available"
                      : health.kernel?.healthy
                        ? "Centralized mode"
                        : health.convex?.healthy
                          ? "Decentralized mode"
                          : "No backends connected"}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.aside>
 
        {/* Main Content */}
        <main className="flex flex-1 flex-col overflow-hidden">
          {/* Header */}
          <header className="flex h-16 items-center justify-between border-b border-[hsl(var(--border))] px-6">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold capitalize">{activeTab.replace("-", " ")}</h1>
              <RoleBadge role={authLevel} showIcon={false} />
            </div>
            <div className="flex items-center gap-4">
              {authLevel === "user" && (
                <button
                  onClick={() => setShowRegistration(true)}
                  className="hidden sm:flex items-center gap-2 rounded bg-[hsl(var(--primary)/.1)] px-3 py-1.5 text-sm text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/.2)] transition-colors cursor-pointer"
                >
                  <User className="h-3.5 w-3.5" />
                  <span>Profile</span>
                </button>
              )}
              {/* Close button for mobile */}
              <button
                onClick={toggleSidebar}
                className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(var(--muted))] cursor-pointer lg:hidden"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </header>
 
          {/* Content Area */}
          <div className="flex-1 overflow-auto p-6">
            <AnimatePresence mode="wait">
              {/* Admin Views */}
              {(authLevel === "master" || authLevel === "creator") && (
                <>
                  {activeTab === "dashboard" && (
                    <motion.div initial={{ opacity:0, y: 20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y: -20 }} className="h-full">
                      <DashboardView />
                    </motion.div>
                  )}
                  {activeTab === "playground" && (
                    <motion.div initial={{ opacity:0, y: 20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y: -20 }} className="h-full">
                      <PlaygroundView />
                    </motion.div>
                  )}
                  {activeTab === "mcp" && (
                    <motion.div initial={{ opacity:0, y: 20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y: -20 }} className="h-full">
                      <McpServersView />
                    </motion.div>
                  )}
                  {activeTab === "management" && (
                    <motion.div initial={{ opacity:0, y: 20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y: -20 }} className="h-full">
                      <OperatorsView />
                    </motion.div>
                  )}
                  {activeTab === "data" && (
                    <motion.div initial={{ opacity:0, y: 20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y: -20 }} className="h-full">
                      <DataView />
                    </motion.div>
                  )}
                  {activeTab === "patterns" && (
                    <motion.div initial={{ opacity:0, y: 20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y: -20 }} className="h-full">
                      <PatternsView />
                    </motion.div>
                  )}
                  {activeTab === "discovery" && (
                    <motion.div initial={{ opacity:0, y: 20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y: -20 }} className="h-full">
                      <DiscoveryView />
                    </motion.div>
                  )}
                  {activeTab === "evolution" && (
                    <motion.div initial={{ opacity:0, y: 20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y: -20 }} className="h-full">
                      <EvolutionView />
                    </motion.div>
                  )}
                  {activeTab === "logs" && (
                    <motion.div initial={{ opacity:0, y: 20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y: -20 }} className="h-full">
                      <ActionLogView />
                    </motion.div>
                  )}
                  {activeTab === "apikeys" && (
                    <motion.div initial={{ opacity:0, y: 20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y: -20 }} className="h-full">
                      <AdminApiKeysView />
                    </motion.div>
                  )}
                  {activeTab === "policies" && (
                    <motion.div initial={{ opacity:0, y: 20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y: -20 }} className="h-full">
                      <PoliciesView />
                    </motion.div>
                  )}
                  {activeTab === "security" && (
                    <motion.div initial={{ opacity:0, y: 20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y: -20 }} className="h-full">
                      <SecurityView />
                    </motion.div>
                  )}
                  {activeTab === "settings" && (
                    <motion.div initial={{ opacity:0, y: 20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y: -20 }} className="h-full">
                      <SettingsView />
                    </motion.div>
                  )}
                </>
              )}
              
              {/* User Views */}
              {(authLevel === "user" || authLevel === "passkey") && (
                <>
                  {activeTab === "user-dashboard" && (
                    <motion.div initial={{ opacity:0, y: 20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y: -20 }} className="h-full">
                      <UserDashboardView />
                    </motion.div>
                  )}
                  {activeTab === "playground" && (
                    <motion.div initial={{ opacity:0, y: 20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y: -20 }} className="h-full">
                      <PlaygroundView />
                    </motion.div>
                  )}
                  {activeTab === "my-api-keys" && (
                    <motion.div initial={{ opacity:0, y: 20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y: -20 }} className="h-full">
                      <MyApiKeysView />
                    </motion.div>
                  )}
                  {activeTab === "user-settings" && (
                    <motion.div initial={{ opacity:0, y: 20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y: -20 }} className="h-full">
                      <UserSettingsView />
                    </motion.div>
                  )}
                </>
              )}
            </AnimatePresence>
          </div>
        </main>
      </div>
    </UnifiedAuthGate>
  );
}
